import { axiosTassoQuintoApiInstance } from '../tasso-quinto';
import {
  AcquisitionContact,
  AcquisitionOperation,
  AcquisitionYesNo,
} from './acquisitionOperation';

const CDQ_LEADS_PATH = '/v1/backoffice/cessioni-del-quinto/leads';

/**
 * Whom a Cessione del quinto can be acquired for. `autonomo` is deliberately
 * absent: the public funnel still offers it only to reject it, and the backend
 * schema refuses it with `400 invalid_request`.
 */
export type CdqOccupation =
  | 'dipendente_pubblico'
  | 'dipendente_privato'
  | 'dipendente_parapubblico'
  | 'pensionato';

export type CdqTenureBand = '1 anno o meno' | '2 anni' | '3 anni o più';

export type CdqEmployeesNumber = 'Meno di 15 dip' | 'Più di 15 dip';

/** How many times a year the salary is paid. */
export type CdqSalaryInstalments = 12 | 13 | 14;

/**
 * The flat CdQ acquisition command, exactly as the closed backend schema
 * accepts it. It keeps the public funnel's field names and canonical values,
 * but not its representation: `importo`, `anni`, `stipendio`,
 * `redditoMensilePensionato` and `mensilita` are JSON numbers here, where the
 * public funnel sends numeric strings.
 *
 * `approfondisci` has no place in this type. It is server-owned: the backend
 * imposes `false` on the stored lead, and sending it — either value — is an
 * unknown key the closed schema rejects.
 */
export interface CreateCdqLeadCommand extends AcquisitionContact {
  /** Whole euros, €2.000–€75.000. */
  importo: number;
  occupazione: CdqOccupation;
  /** Whole years, 18–100. */
  anni: number;
  spid: AcquisitionYesNo;
  cittadinanzaItaliana: AcquisitionYesNo;
  altriPrestitiInCorso: AcquisitionYesNo;
  ritardiPagamenti: AcquisitionYesNo;
  /** Whole euros, €100–€500.000; everyone but a pensioner. */
  stipendio?: number;
  /** Whole euros, €100–€500.000; pensioners only. */
  redditoMensilePensionato?: number;
  /** Everyone but a pensioner. */
  indeterminato?: AcquisitionYesNo;
  /** Everyone but a pensioner. */
  mensilita?: CdqSalaryInstalments;
  /** Everyone but a pensioner. */
  tempo?: CdqTenureBand;
  /** Private and parapublic employees only. */
  nDipendenti?: CdqEmployeesNumber;
}

/**
 * Starts the assisted CdQ acquisition: as for Prestito, Tasso Quinto owns the
 * product lead, the HubSpot Deal, the Area Clienti account and the credential
 * email behind this one authenticated command.
 *
 * The idempotency identity includes the product, so the same key on the
 * Prestito route names a different operation: two products compiled side by
 * side can never be mistaken for one another.
 */
export const createCdqLead = async (
  command: CreateCdqLeadCommand,
  idempotencyKey: string
): Promise<AcquisitionOperation> => {
  const { data } = await axiosTassoQuintoApiInstance.post<AcquisitionOperation>(
    CDQ_LEADS_PATH,
    command,
    { headers: { 'Idempotency-Key': idempotencyKey } }
  );
  return data;
};
